import React, { useEffect, useRef, useState } from 'react';
import type { AtsDesign, AtsLine, AtsResumeDoc } from '../../lib/api';
import { diffWords } from './diff';

// The ATS resume as a page that looks like its PDF, in the chosen design.
// Click a line to edit it (undo puts the original wording back); "Show
// changes" marks the words the AI changed. Names, employers, dates,
// contact details and education are fixed — the server keeps them as in
// the original resume.

type Doc = AtsResumeDoc;
type Upd = (f: (d: Doc) => Doc) => void;

// The same three looks as the files (services/atsDocs.js on the server)
export const LOOK: Record<AtsDesign, { label: string; blurb: string; accent: string; rule: string; ruleW: number; name: number; headline: number; body: number; head: number; pad: string; section: string; item: string; leading: string }> = {
  classic: { label: 'Classic', blurb: 'Black and grey, timeless', accent: '#111111', rule: '#9ca3af', ruleW: 1, name: 26, headline: 14, body: 13, head: 13, pad: 'px-7 sm:px-12 py-10', section: 'mt-5', item: 'mt-2.5', leading: 'leading-[1.5]' },
  modern:  { label: 'Modern',  blurb: 'A navy name and headings', accent: '#1F3A5F', rule: '#1F3A5F', ruleW: 2, name: 30, headline: 14.5, body: 13, head: 13, pad: 'px-7 sm:px-12 py-11', section: 'mt-6', item: 'mt-3', leading: 'leading-[1.55]' },
  compact: { label: 'Compact', blurb: 'Smaller, fits one page', accent: '#111111', rule: '#a3a3a3', ruleW: 1, name: 21, headline: 12.5, body: 12, head: 12, pad: 'px-6 sm:px-9 py-7', section: 'mt-3', item: 'mt-1.5', leading: 'leading-[1.4]' },
};

const HEADINGS: Record<string, string> = {
  summary: 'Professional Summary', skills: 'Skills', experience: 'Experience', projects: 'Projects',
  education: 'Education', certifications: 'Certifications', achievements: 'Achievements',
};

const DiffText: React.FC<{ before: string; after: string }> = ({ before, after }) => (
  <>
    {diffWords(before, after).map((p, i) => (
      <React.Fragment key={i}>
        {i > 0 && ' '}
        {p.kind === 'same' ? p.text
          : p.kind === 'add' ? <mark className="bg-yellow-200/90 text-inherit rounded px-0.5">{p.text}</mark>
            : <del className="text-red-400 decoration-red-300 text-[0.92em]">{p.text}</del>}
      </React.Fragment>
    ))}
  </>
);

// One line of text: shown like the PDF; a click turns it into a box
const Line: React.FC<{
  line: { text: string; original: string }; show: boolean; readOnly?: boolean;
  onChange: (text: string) => void; onRemove?: () => void; bullet?: boolean; style?: React.CSSProperties; className?: string; testId?: string;
}> = ({ line, show, readOnly, onChange, onRemove, bullet, style, className = '', testId }) => {
  const [editing, setEditing] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const changed = line.original !== '' && line.text !== line.original;
  const isNew = line.original === '';

  useEffect(() => {
    const el = box.current;
    if (!editing || !el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [editing, line.text]);

  const shown = show && changed ? <DiffText before={line.original} after={line.text} />
    : show && isNew ? <mark className="bg-green-100 text-inherit rounded px-0.5">{line.text}</mark>
      : line.text;

  return (
    <div className={`relative flex gap-2 ${className}`} style={style} data-line={testId}>
      {bullet && <span className="select-none">•</span>}
      {editing ? (
        <div ref={wrap} className="flex-1 min-w-0" onBlur={e => { if (!wrap.current?.contains(e.relatedTarget as Node)) setEditing(false); }}>
          <textarea ref={box} autoFocus value={line.text} onChange={e => onChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape' || (e.key === 'Enter' && !e.shiftKey)) { e.preventDefault(); setEditing(false); } }}
            className="w-full px-2 py-1 -mx-2 rounded-lg border-2 border-purple-300 bg-purple-50/40 focus:outline-none resize-none" style={{ font: 'inherit', lineHeight: 'inherit' }} />
          <div className="flex flex-wrap items-center gap-2 mt-1 font-sans" style={{ fontSize: 11 }}>
            <button onClick={() => setEditing(false)} className="px-2.5 py-1 rounded-lg font-extrabold text-white bg-[#2b2240]">Done</button>
            {changed && <button onClick={() => { onChange(line.original); setEditing(false); }} className="px-2.5 py-1 rounded-lg font-extrabold text-purple-800 bg-purple-100">↩ Undo the AI's change</button>}
            {onRemove && <button onClick={() => { setEditing(false); onRemove(); }} className="px-2.5 py-1 rounded-lg font-extrabold text-red-600 bg-red-50">Remove line</button>}
            {changed && <span className="text-gray-400 flex-1 min-w-[12rem]"><b>Before:</b> {line.original}</span>}
          </div>
        </div>
      ) : (
        <span onClick={() => !readOnly && setEditing(true)} title={readOnly ? undefined : 'Click to edit'}
          className={`flex-1 min-w-0 rounded transition-colors ${readOnly ? '' : 'cursor-text hover:bg-purple-50 hover:shadow-[0_0_0_3px_#f5f3ff]'}`}>
          {shown || <span className="text-gray-300 italic">Empty</span>}
        </span>
      )}
    </div>
  );
};

// Small controls on an entry: move up / down, remove
const EntryTools: React.FC<{ onUp?: () => void; onDown?: () => void; onRemove?: () => void }> = ({ onUp, onDown, onRemove }) => (
  <span className="absolute -right-2 -top-1 flex gap-0.5 font-sans sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-white rounded-lg shadow-md border border-gray-100 px-0.5">
    {onUp && <button onClick={onUp} title="Move up" className="w-6 h-6 text-xs text-gray-500 hover:text-purple-700">↑</button>}
    {onDown && <button onClick={onDown} title="Move down" className="w-6 h-6 text-xs text-gray-500 hover:text-purple-700">↓</button>}
    {onRemove && <button onClick={onRemove} title="Remove" className="w-6 h-6 text-xs text-gray-400 hover:text-red-600">✕</button>}
  </span>
);

const Locked: React.FC = () => (
  <span title="Copied from the original resume — never changed" className="font-sans text-[10px] ml-1.5 opacity-0 group-hover:opacity-60 transition-opacity select-none">🔒</span>
);

const moveIn = <T,>(list: T[], i: number, d: number) => {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const n = [...list];
  [n[i], n[j]] = [n[j], n[i]];
  return n;
};

const SkillLine: React.FC<{ group: string; items: string[]; readOnly?: boolean; onChange: (group: string, items: string[]) => void }> = ({ group, items, readOnly, onChange }) => {
  const [editing, setEditing] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  if (editing) {
    return (
      <div ref={wrap} className="flex flex-col sm:flex-row gap-1.5 font-sans" onBlur={e => { if (!wrap.current?.contains(e.relatedTarget as Node)) setEditing(false); }}>
        <input autoFocus value={group} onChange={e => onChange(e.target.value, items)} className="sm:w-40 px-2 py-1 rounded-lg border-2 border-purple-300 text-xs font-bold focus:outline-none" />
        <input value={items.join(', ')} onChange={e => onChange(group, e.target.value.split(',').map(s => s.trimStart()))}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') setEditing(false); }}
          className="flex-1 px-2 py-1 rounded-lg border-2 border-purple-300 text-xs focus:outline-none" />
        <button onClick={() => setEditing(false)} className="px-2.5 py-1 rounded-lg text-[11px] font-extrabold text-white bg-[#2b2240]">Done</button>
      </div>
    );
  }
  return (
    <p onClick={() => !readOnly && setEditing(true)} className={`rounded ${readOnly ? '' : 'cursor-text hover:bg-purple-50'}`}>
      <b>{group}:</b> {items.filter(Boolean).join(', ')}
    </p>
  );
};

const ResumePaper: React.FC<{ doc: Doc; design: AtsDesign; showChanges: boolean; readOnly?: boolean; onChange?: Upd }> = ({ doc, design, showChanges, readOnly, onChange }) => {
  const L = LOOK[design] ?? LOOK.classic;
  const upd: Upd = f => onChange?.(f);
  const setBullet = (key: 'experience' | 'projects', ei: number, bi: number, text: string) =>
    upd(d => ({ ...d, [key]: d[key].map((e: any, i: number) => (i !== ei ? e : { ...e, bullets: e.bullets.map((b: AtsLine, j: number) => (j === bi ? { ...b, text } : b)) })) }));
  const dropBullet = (key: 'experience' | 'projects', ei: number, bi: number) =>
    upd(d => ({ ...d, [key]: d[key].map((e: any, i: number) => (i !== ei ? e : { ...e, bullets: e.bullets.filter((_: AtsLine, j: number) => j !== bi) })) }));

  const Heading: React.FC<{ children: React.ReactNode; locked?: boolean }> = ({ children, locked }) => (
    <div className={`group ${L.section} mb-1.5`}>
      <p className="font-bold uppercase flex items-center" style={{ color: L.accent, fontSize: L.head, letterSpacing: '0.06em' }}>{children}{locked && <Locked />}</p>
      <div style={{ height: L.ruleW, background: L.rule, marginTop: 2 }} />
    </div>
  );
  const dates = (a: string, b: string) => [a, b].filter(Boolean).join(' – ');

  return (
    <div className={`bg-white text-[#111] mx-auto w-full max-w-[860px] ${L.pad} ${L.leading} rounded-md shadow-[0_2px_6px_rgba(0,0,0,.06),0_24px_60px_-24px_rgba(43,34,64,.45)] border border-gray-100`}
      style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', fontSize: L.body }} data-design={design}>
      <p className="font-bold leading-tight" style={{ color: L.accent, fontSize: L.name }}>{doc.name}</p>
      <Line line={doc.headline} show={showChanges} readOnly={readOnly} testId="headline" className="mt-0.5 text-[#333]" style={{ fontSize: L.headline }}
        onChange={text => upd(d => ({ ...d, headline: { ...d.headline, text } }))} />
      <p className="group mt-1 text-[#333] break-words" style={{ fontSize: L.body - 1 }}>
        {[doc.email, doc.phone, doc.location, ...doc.links].filter(Boolean).join('  |  ')}<Locked />
      </p>

      {doc.sectionOrder.map(section => {
        if (section === 'summary' && doc.summary.text) return (
          <div key={section}>
            <Heading>{HEADINGS.summary}</Heading>
            <Line line={doc.summary} show={showChanges} readOnly={readOnly} testId="summary" onChange={text => upd(d => ({ ...d, summary: { ...d.summary, text } }))} />
          </div>
        );
        if (section === 'skills' && doc.skills.length) return (
          <div key={section}>
            <Heading>{HEADINGS.skills}</Heading>
            <div className="space-y-0.5">
              {doc.skills.map((g, gi) => (
                <SkillLine key={gi} group={g.group} items={g.items} readOnly={readOnly}
                  onChange={(group, items) => upd(d => ({ ...d, skills: d.skills.map((x, i) => (i === gi ? { group, items } : x)) }))} />
              ))}
            </div>
          </div>
        );
        if (section === 'experience' && doc.experience.length) return (
          <div key={section}>
            <Heading>{HEADINGS.experience}</Heading>
            {doc.experience.map((e, ei) => (
              <div key={e.id} className={`group relative ${ei ? L.item : ''}`}>
                {!readOnly && <EntryTools onUp={ei > 0 ? () => upd(d => ({ ...d, experience: moveIn(d.experience, ei, -1) })) : undefined}
                  onDown={ei < doc.experience.length - 1 ? () => upd(d => ({ ...d, experience: moveIn(d.experience, ei, 1) })) : undefined} />}
                <div className="flex justify-between gap-3">
                  <p className="font-bold">{[e.role, e.company].filter(Boolean).join(', ')}{e.location ? ` — ${e.location}` : ''}<Locked /></p>
                  <span className="shrink-0">{dates(e.start, e.end)}</span>
                </div>
                {e.bullets.map((b, bi) => (
                  <Line key={b.id} line={b} bullet show={showChanges} readOnly={readOnly} testId={b.id} className="pl-1"
                    onChange={t => setBullet('experience', ei, bi, t)} onRemove={() => dropBullet('experience', ei, bi)} />
                ))}
              </div>
            ))}
          </div>
        );
        if (section === 'projects' && doc.projects.length) return (
          <div key={section}>
            <Heading>{HEADINGS.projects}</Heading>
            {doc.projects.map((p, pi) => (
              <div key={p.id} className={`group relative ${pi ? L.item : ''}`} data-project={p.id}>
                {!readOnly && <EntryTools onUp={pi > 0 ? () => upd(d => ({ ...d, projects: moveIn(d.projects, pi, -1) })) : undefined}
                  onDown={pi < doc.projects.length - 1 ? () => upd(d => ({ ...d, projects: moveIn(d.projects, pi, 1) })) : undefined}
                  onRemove={() => upd(d => ({ ...d, projects: d.projects.filter((_, i) => i !== pi) }))} />}
                <p className="font-bold">
                  {showChanges && p.source !== 'resume' ? <mark className="bg-green-100 text-inherit rounded px-0.5">{p.name}</mark> : p.name}
                  {p.source !== 'resume' && <span className="font-sans ml-2 align-middle text-[9px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">{p.source === 'chat' ? 'Added in chat' : 'Added'}</span>}
                </p>
                {(p.tech.length > 0 || p.link) && <p className="italic text-[#444]" style={{ fontSize: L.body - 1 }}>{[p.tech.join(', '), p.link].filter(Boolean).join(' | ')}</p>}
                {p.bullets.map((b, bi) => (
                  <Line key={b.id} line={b} bullet show={showChanges} readOnly={readOnly} testId={b.id} className="pl-1"
                    onChange={t => setBullet('projects', pi, bi, t)} onRemove={() => dropBullet('projects', pi, bi)} />
                ))}
              </div>
            ))}
          </div>
        );
        if (section === 'education' && doc.education.length) return (
          <div key={section}>
            <Heading locked>{HEADINGS.education}</Heading>
            {doc.education.map((e, i) => (
              <div key={i} className={i ? L.item : ''}>
                <div className="flex justify-between gap-3">
                  <p className="font-bold">{[e.degree, e.field].filter(Boolean).join(' in ')}</p>
                  <span className="shrink-0">{dates(e.start, e.end)}</span>
                </div>
                <p>{[[e.institution, e.location].filter(Boolean).join(', '), e.score].filter(Boolean).join(' | ')}</p>
              </div>
            ))}
          </div>
        );
        if (section === 'certifications' && doc.certifications.length) return (
          <div key={section}>
            <Heading>{HEADINGS.certifications}</Heading>
            {doc.certifications.map((c, i) => (
              <div key={c.id} className="group relative flex gap-2">
                <span>•</span>
                <span className="flex-1">{[c.name, c.issuer].filter(Boolean).join(' — ')}{c.year ? ` (${c.year})` : ''}</span>
                {!readOnly && <EntryTools onRemove={() => upd(d => ({ ...d, certifications: d.certifications.filter((_, j) => j !== i) }))} />}
              </div>
            ))}
          </div>
        );
        if (section === 'achievements' && doc.achievements.length) return (
          <div key={section}>
            <Heading>{HEADINGS.achievements}</Heading>
            {doc.achievements.map((a, i) => (
              <Line key={a.id} line={a} bullet show={showChanges} readOnly={readOnly} testId={a.id}
                onChange={text => upd(d => ({ ...d, achievements: d.achievements.map((x, j) => (j === i ? { ...x, text } : x)) }))}
                onRemove={() => upd(d => ({ ...d, achievements: d.achievements.filter((_, j) => j !== i) }))} />
            ))}
          </div>
        );
        if (section === 'extra') return doc.extra.map((x, i) => (
          <div key={`extra${i}`}>
            <Heading>{x.heading}</Heading>
            {x.items.map((it, j) => <div key={j} className="flex gap-2"><span>•</span><span>{it}</span></div>)}
          </div>
        ));
        return null;
      })}
    </div>
  );
};

export default ResumePaper;
